import { Injectable } from '@angular/core';
import { IndexedDBService } from './indexed-db.service';

@Injectable({
    providedIn: 'root'
})
export class CacheService {
    private localStorageKeys = [
        'kc-token',
        'kc-refresh-token',
        'kc-user-info',
        'kc-expires-at',
        'access-token',
        'refresh-token',
        'user-info',
        'user-credentials',
        'auth-info',
        'keycloak-token',
		'user-list'
    ];

    constructor(private indexedDBService: IndexedDBService) { }

    async setItem(key: string, value: any): Promise<void> {
        const stringValue = typeof value === 'string' ? value : JSON.stringify(value);

        if (this.shouldUseLocalStorage(key)) {
            localStorage.setItem(key, stringValue);
        } else {
            const storeName = this.getStoreName(key);
            await this.indexedDBService.setItem(storeName, key, value);
        }
    }

    async getItem(key: string): Promise<any> {
        if (this.shouldUseLocalStorage(key)) {
            const value = localStorage.getItem(key);
            try {
                return value ? JSON.parse(value) : null;
            } catch {
                return value;
            }
        } else {
            const storeName = this.getStoreName(key);
            return await this.indexedDBService.getItem(storeName, key);
        }
    }

    async removeItem(key: string): Promise<void> {
        if (this.shouldUseLocalStorage(key)) {
            localStorage.removeItem(key);
        } else {
            const storeName = this.getStoreName(key);
            await this.indexedDBService.removeItem(storeName, key);
        }
    }

    async clear(): Promise<void> {
        localStorage.clear();
        await this.indexedDBService.clear('formData');
        await this.indexedDBService.clear('userData');
        await this.indexedDBService.clear('lastInsert');
    }

    private shouldUseLocalStorage(key: string): boolean {
        return this.localStorageKeys.some(lsKey => key.includes(lsKey) || key.startsWith(lsKey));
    }

    private getStoreName(key: string): string {
        if (key.includes('formFields') || key.includes('formNames')) {
            return 'formData';
        }
        if (key.includes('user-list')) {
            return 'userData';
        }
        if (key.includes('last-insert')) {
            return 'lastInsert';
        }
        return 'formData';
    }

    async setFormFields(formFields: any): Promise<void> {
        await this.setItem('formFields', formFields);
    }

    async getFormFields(): Promise<any> {
        return await this.getItem('formFields');
    }

    async setFormNames(formNames: string[]): Promise<void> {
        await this.setItem('formNames', formNames);
    }

    async getFormNames(): Promise<string[]> {
        return await this.getItem('formNames');
    }

    async setUserList(userList: any[]): Promise<void> {
        await this.setItem('user-list', userList);
    }

    async getUserList(): Promise<any[]> {
        return await this.getItem('user-list');
    }

    async setLastInsert(type: string, data: any): Promise<void> {
        await this.setItem(`last-insert-${type}`, data);
    }

    async getLastInsert(type: string): Promise<any> {
        return await this.getItem(`last-insert-${type}`);
    }
}
